// ACHAR O QUADRINHO DA URL NO ACERVO — e a terceira vez que este produto responde a mesma pergunta.
//
// ── O DEFEITO, MEDIDO TRES VEZES EM TRES ROTAS ──────────────────────────────────────────────
//
// O `T8.4`, na letra dele: *"abrir a mesma URL diretamente (F5, link salvo, nova aba) mostra uma
// página sem estilo nenhum dizendo que o quadrinho não existe, mesmo com ele existindo."*
//
// A causa é sempre a mesma, e está escrita por extenso em `editar-quadrinho.tsx`: o acervo chega por
// snapshot do Firestore DEPOIS da montagem, e até chegar a lista é `[]` — que responde "não existe".
// `appUser` é o MESMO objeto para sempre, mutado por dentro, então uma dependência nele nunca
// dispara. Quem depende da LISTA reencontra o quadrinho; quem depende do `appUser` nunca reencontra.
//
// Em 24/09/2026 isso foi consertado no EDITOR. Em 27/09/2026, medindo contra o Firestore local com as
// regras deste repositório, a tela de PUBLICAR abriu por URL e disse "Não encontrei esse quadrinho"
// num quadrinho que o editor abria ao lado, com as sete imagens dele na tela: o `useMemo` dela
// declarava `[appUser, id]` — exatamente as duas linhas que o editor tinha antes.
//
// A terceira rota, VISUALIZAR, acerta **por acidente de forma**: ela lê `Comics()` durante o render,
// então reencontra a cada repintura. Acidente que funciona é o que faz o defeito voltar na rota
// seguinte, porque não há nada escrito para copiar.
//
// Por isso a decisão sai daqui, uma vez, como função PURA — e o que cada tela faz com ela é o que
// difere entre elas. O editor guarda uma CÓPIA (ele muta o quadrinho, e refazer a cópia a cada render
// descartaria o desenho a cada tecla); publicar e visualizar usam o objeto do acervo.

/**
 * Quanto se espera o acervo antes de dizer que o quadrinho não existe.
 *
 * Oito segundos é bem mais que o tempo medido de chegada do snapshot em produção (abaixo de 2 s) e
 * bem menos que a paciência de quem está olhando — e o que acontece ao fim dele é uma RESPOSTA, não
 * silêncio. Esperar para sempre é a outra forma de não responder, e é o defeito do `T8.12`.
 */
export const ESPERA_DO_ACERVO_MS = 8000;

/**
 * O QUE ESTA DECISÃO PRECISA DE UM QUADRINHO, e é só isto: o id dele.
 *
 * Tipar por forma e não pela classe `Comic` não é estética — `comic.class.tsx` importa `react-konva`,
 * que em Node exige o pacote nativo `canvas`, e o teste desta decisão morreria em
 * "Cannot find module 'canvas'" sem nunca chegar a avaliar uma linha dela. Uma regra de navegação não
 * deveria precisar de uma tela de desenho para ser medida.
 */
export type TemId = { getId(): string };

export type Resolucao<T extends TemId> =
  /** O acervo ainda pode chegar. A tela DIZ que está abrindo — ausência não é estado. */
  | { estado: "esperando" }
  /** Achei. */
  | { estado: "achei"; comic: T }
  /** Não existe, e isto é uma resposta: ou sumiu da lista, ou o tempo do acervo acabou. */
  | { estado: "nao-existe" };

/**
 * A DECISÃO, SEM RELÓGIO E SEM REACT.
 *
 * `jaTinha` é o id que esta tela já teve na mão. Ele existe para separar duas coisas que se parecem
 * na tela e são opostas: *"o acervo ainda não chegou"* e *"o quadrinho sumiu da lista"* — o segundo
 * caso (excluído noutra aba) é "não existe" na hora, sem esperar oito segundos por nada.
 *
 * `esgotou` é o que a tela sabe sobre o relógio dela, e vem de fora justamente para esta função não
 * ter nenhum.
 */
export function resolverQuadrinho<T extends TemId>(
  comics: T[],
  id: string | undefined,
  jaTinha: string | null,
  esgotou: boolean
): Resolucao<T> {
  // Sem id na URL não há o que achar, e quem chama é que decide o que fazer com isso (o editor abre
  // um rascunho novo). Aqui é "não existe" e é honesto.
  if (!id) return { estado: "nao-existe" };

  const achado = comics.find((c) => c.getId() === id);
  if (achado) return { estado: "achei", comic: achado };

  if (jaTinha === id) return { estado: "nao-existe" };

  return esgotou ? { estado: "nao-existe" } : { estado: "esperando" };
}
